import React, { useMemo } from "react";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import { buildUser, buildWorkerProfile } from "../../factories";
import PostOnbaridngProfileEditorScreen from "../../../screens/profile-editor/PostOnboardingProfileEditor";
import {
    BankAccount,
    DocumentType,
    Gender,
    MaritalStatus,
    Person,
    ProfileType,
    WorkingPermitType,
} from "../../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import { WorkingPermit } from "../../../__generated__/graphql";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import CivilStateEditorScreen from "../../../screens/profile-editor/CivilStateEditor";
import FiscalAttestationEditorScreen from "../../../screens/profile-editor/FiscalAttestationEditor";
import ChildrenEditorScreen from "../../../screens/profile-editor/ChildrenEditor";
import BankAccountEditorScreen from "../../../screens/profile-editor/BankAccountEditor";
import { MockedProvider } from "@apollo/client/testing/react";
import {
    MockedProfileSwitcherManager,
    ProfileIdType,
} from "../../../contexts/ProfileSwitcherManager";
import { StoryStackNavigatorContainer } from "../../display-helpers";

const Stack = createNativeStackNavigator<{
    PostOnboardingProfileEditor: undefined;
    CivilStateEdit: undefined;
    FiscalAttestationEdit: undefined;
    ChildrenEdit: undefined;
    BankAccountEditModal: { profileId: ProfileIdType };
}>();

type StoryComponentProps = {
    nationality?: string;
    maritalStatus?: MaritalStatus | null;
    hasOtherActivity?: boolean;
    hasPermit?: boolean;
    workPermit?: WorkingPermitType | null;
    partner?: Person | null;
    dataChildren?: Person[] | null;
    hasFiscalAttestation?: boolean;
    bankAccount?: BankAccount | null;
    avsNumber?: string | null;
};

const StoryComponent = ({
    nationality,
    maritalStatus,
    hasOtherActivity,
    hasPermit,
    workPermit,
    partner,
    dataChildren,
    hasFiscalAttestation,
    bankAccount,
    avsNumber,
}: StoryComponentProps) => {
    const user = useMemo(
        () =>
            buildUser({
                nationality,
                workerProfile: buildWorkerProfile({
                    avsNumber,
                    bankAccount,
                    partner,
                    swissOrHasPermitAnswer: hasPermit === undefined ? !!workPermit : hasPermit,
                    lastWorkPermit: workPermit
                        ? {
                              type: workPermit,
                              validFrom: null,
                              validTo: null,
                          }
                        : null,
                    maritalStatus: maritalStatus || (partner ? MaritalStatus.Married : null),
                    hasOtherActivity,
                    children: dataChildren,
                    hasChildren: Array.isArray(dataChildren) ? dataChildren.length > 0 : undefined,
                    hasFiscalAttestation,
                    documents: hasFiscalAttestation
                        ? [
                              {
                                  id: "document",
                                  type: DocumentType.FiscalAttestation,
                                  title: "Attestation.pdf",
                                  documentUrl: "",
                                  asset: null,
                                  inlineContent: null,
                              },
                          ]
                        : undefined,
                }),
            }),
        [
            nationality,
            hasPermit,
            workPermit,
            avsNumber,
            bankAccount,
            partner,
            maritalStatus,
            hasOtherActivity,
            dataChildren,
            hasFiscalAttestation,
        ],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={ProfileType.Worker}>
                <MockedProvider mocks={[]}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="PostOnboardingProfileEditor">
                            <Stack.Screen
                                name="PostOnboardingProfileEditor"
                                component={PostOnbaridngProfileEditorScreen}
                            />
                            <Stack.Screen
                                name="CivilStateEdit"
                                component={CivilStateEditorScreen}
                            />
                            <Stack.Screen
                                name="FiscalAttestationEdit"
                                component={FiscalAttestationEditorScreen}
                            />
                            <Stack.Screen name="ChildrenEdit" component={ChildrenEditorScreen} />
                            <Stack.Screen
                                name="BankAccountEditModal"
                                /* @ts-expect-error Component need a complex navigation handler */
                                component={BankAccountEditorScreen}
                            />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/ProfileEditor/PostOnbardingProfileEditor",
    component: StoryComponent,
    argTypes: {
        workPermit: {
            options: Object.values(WorkingPermit),
            control: { type: "radio" },
        },
        dataChildren: {
            control: { type: "object" },
        },
    },
    args: {
        workPermit: WorkingPermitType.PermitB,
        dataChildren: null,
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

const dataChildren: Person[] = [
    {
        id: "children1",
        firstName: "Kylian",
        lastName: "Mbappé",
        avsNumber: "756.1234.5678.97",
        birthdate: "2020-01-01",
        gender: Gender.Male,
        monthlySalary: null,
        inFirstEducation: null,
        nationality: null,
        permitType: null,
    },
];

const partner: Person = {
    id: "partner",
    firstName: "Mona",
    lastName: "Lisa",
    avsNumber: "756.1234.5678.97",
    birthdate: "1503-05-12",
    gender: Gender.Female,
    monthlySalary: 0,
    inFirstEducation: null,
    nationality: null,
    permitType: null,
};

const defaultBankAccount = {
    id: "bankAccount",
    name: "My bank",
    iban: "CH9789144829733648596",
    bic: "POFICHBEXXX",
    beneficiary: "",
};

export const Empty: Story = {
    args: {
        nationality: "CH",
        maritalStatus: null,
        bankAccount: null,
        hasOtherActivity: false,
        workPermit: null,
        avsNumber: null,
    },
};

export const SwissWithoutChildren: Story = {
    args: {
        maritalStatus: MaritalStatus.Single,
        nationality: "CH",
    },
};

export const BPermitWithPartnerAndChildren: Story = {
    args: {
        workPermit: WorkingPermitType.PermitB,
        dataChildren,
        partner,
        hasOtherActivity: false,
    },
};

export const CPermitWithPartner: Story = {
    args: {
        workPermit: WorkingPermitType.PermitC,
        partner,
        hasOtherActivity: true,
    },
};

export const GPermitWithFiscalAttestation: Story = {
    args: {
        maritalStatus: MaritalStatus.Single,
        workPermit: WorkingPermitType.PermitG,
        hasFiscalAttestation: true,
    },
};

export const SingleSwissComplete: Story = {
    args: {
        nationality: "CH",
        maritalStatus: MaritalStatus.Single,
        bankAccount: defaultBankAccount,
        hasOtherActivity: false,
    },
};

export const BPermitComplete: Story = {
    args: {
        workPermit: WorkingPermitType.PermitB,
        dataChildren,
        maritalStatus: MaritalStatus.Single,
        hasOtherActivity: true,
        bankAccount: defaultBankAccount,
    },
};

export const FrenchWithoutPermit: Story = {
    args: {
        nationality: "FR",
        maritalStatus: MaritalStatus.Single,
        bankAccount: defaultBankAccount,
        hasOtherActivity: false,
        workPermit: null,
        avsNumber: null,
    },
};

export const FrenchWithUnknownPermit: Story = {
    args: {
        nationality: "FR",
        bankAccount: null,
        maritalStatus: MaritalStatus.Single,
        hasOtherActivity: false,
        hasPermit: true,
        workPermit: null,
        avsNumber: null,
    },
};
